export type AccessLevel = "none" | "read" | "full";

export type PermissionDomain =
  | "dashboard"
  | "visits"
  | "badges"
  | "badgeTemplates"
  | "badgeRequests"
  | "security"
  | "catalogs"
  | "people"
  | "reports"
  | "devices"
  | "settings";

export type BadgeCreationKind = "permanent" | "visitor";
export type PersonCategory =
  | "PRESIDENCY_STAFF"
  | "EXTERNAL_STAFF"
  | "MILITARY"
  | "CONTRACTOR"
  | "VISITOR";

export type RoleScope = "CENTRAL" | "ORGANIZATION";
export type RoleCreationSurface = "PEOPLE" | "ADMIN";
export type ScopeAccess = "none" | "own" | "organization" | "any";
export type AppShellVariant = "DEFAULT" | "SENTINEL" | "EMPLOYEE";

export type PermissionPolicy = {
  scope: RoleScope;
  shell: AppShellVariant;
  badgesPageVisible: boolean;
  visitsRead: ScopeAccess;
  visitsCreate: ScopeAccess;
  visitsEdit: ScopeAccess;
  badgesRead: ScopeAccess;
  badgeRequestsCreate: ScopeAccess;
  badgeRequestsCancel: ScopeAccess;
  badgeRequestsInbox: boolean;
  badgeRequestsIssueApproved: boolean;
  securityHawkEye: boolean;
  securityReadSensitive: boolean;
  badgesIssue: boolean;
  badgesManageExisting: boolean;
  badgesReturnVisitor: boolean;
  badgeInitialEnrollmentManage: boolean;
  badgeInitialEnrollmentExecute: boolean;
  badgeRequestsApproveOrg: boolean;
  badgeRequestsApproveCabinet: boolean;
  peopleCreateVisitor: boolean;
  peopleCreateSentinel: boolean;
  devicesManageAssignments: boolean;
  /**
   * Legacy persisted key kept for payload compatibility.
   * Self-editing a linked person profile is forbidden and normalization always
   * resolves this capability to false.
   */
  linkedPersonProfileEdit: boolean;
};

export type BadgeRequestApprovalPermission = "badgeRequestsApproveOrg" | "badgeRequestsApproveCabinet";

export type BadgeRequestApprovalStepDefinition = {
  stepOrder: 1 | 2;
  label: string;
  requiredPermission: BadgeRequestApprovalPermission;
};

export type PermissionPayload = {
  dashboard: AccessLevel;
  visits: AccessLevel;
  badges: AccessLevel;
  badgeTemplates: AccessLevel;
  badgeRequests: AccessLevel;
  security: AccessLevel;
  catalogs: AccessLevel;
  people: AccessLevel;
  reports: AccessLevel;
  devices: AccessLevel;
  settings: AccessLevel;
  admin: boolean;
  policy: PermissionPolicy;
};

export type CanonicalRoleCode =
  | "BADGE_SUPERVISOR"
  | "INSTITUTION_SUPERVISOR"
  | "BADGE_OPERATOR"
  | "SENTINEL"
  | "INSTITUTION_OPERATOR"
  | "MILITARY_SUPERVISOR"
  | "EMPLOYEE"
  | "DSI";

export type PageKey =
  | "dashboard"
  | "visits"
  | "badges"
  | "badgeIssuance"
  | "badgeInitialEnrollment"
  | "badgeRequests"
  | "people"
  | "devices"
  | "hawkEye"
  | "reports"
  | "settings"
  | "settingsCatalog"
  | "accessProfiles"
  | "settingsOrganizations"
  | "settingsBadgeTemplates"
  | "settingsAdministration";

export type PermissionRequirement =
  | { kind: "authenticated" }
  | { kind: "permission"; domain: PermissionDomain; minimum: AccessLevel }
  | { kind: "anyPermission"; checks: readonly { domain: PermissionDomain; minimum: AccessLevel }[] }
  | { kind: "admin" };

export type RoleDefinition = {
  code: CanonicalRoleCode;
  label: string;
  description: string;
  permissions: PermissionPayload;
  badgeCreationKinds?: BadgeCreationKind[];
};

export type RolePolicy = {
  allowedCategories: readonly PersonCategory[];
  scope: RoleScope;
};

export type RoleAssignmentPolicy = {
  peopleCreationRoles: readonly CanonicalRoleCode[];
  canCreateVisitorFromPeople: boolean;
  adminAssignableRoles: readonly CanonicalRoleCode[];
};

export const CANONICAL_ROLE_CODES: readonly CanonicalRoleCode[] = [
  "BADGE_SUPERVISOR",
  "INSTITUTION_SUPERVISOR",
  "BADGE_OPERATOR",
  "SENTINEL",
  "INSTITUTION_OPERATOR",
  "MILITARY_SUPERVISOR",
  "EMPLOYEE",
  "DSI",
] as const;

export const DEFAULT_PERMISSIONS: PermissionPayload = {
  dashboard: "none",
  visits: "none",
  badges: "none",
  badgeTemplates: "none",
  badgeRequests: "none",
  security: "none",
  catalogs: "none",
  people: "none",
  reports: "none",
  devices: "none",
  settings: "none",
  admin: false,
  policy: {
    scope: "CENTRAL",
    shell: "DEFAULT",
    badgesPageVisible: true,
    visitsRead: "none",
    visitsCreate: "none",
    visitsEdit: "none",
    badgesRead: "none",
    badgeRequestsCreate: "none",
    badgeRequestsCancel: "none",
    badgeRequestsInbox: false,
    badgeRequestsIssueApproved: false,
    securityHawkEye: false,
    securityReadSensitive: false,
    badgesIssue: false,
    badgesManageExisting: false,
    badgesReturnVisitor: false,
    badgeInitialEnrollmentManage: false,
    badgeInitialEnrollmentExecute: false,
    badgeRequestsApproveOrg: false,
    badgeRequestsApproveCabinet: false,
    peopleCreateVisitor: false,
    peopleCreateSentinel: false,
    devicesManageAssignments: false,
    linkedPersonProfileEdit: false,
  },
};

export const BADGE_REQUEST_APPROVAL_STEPS: readonly BadgeRequestApprovalStepDefinition[] = [
  {
    stepOrder: 1,
    label: "Validation organisation",
    requiredPermission: "badgeRequestsApproveOrg",
  },
  {
    stepOrder: 2,
    label: "Validation finale badge",
    requiredPermission: "badgeRequestsApproveCabinet",
  },
] as const;

export const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    code: "BADGE_SUPERVISOR",
    label: "Superviseur badge",
    description: "Validation finale des demandes de badge avant impression.",
    permissions: {
      dashboard: "none",
      visits: "none",
      badges: "read",
      badgeTemplates: "none",
      badgeRequests: "full",
      security: "none",
      catalogs: "none",
      people: "none",
      reports: "none",
      devices: "none",
      settings: "none",
      admin: false,
      policy: {
        scope: "CENTRAL",
        shell: "DEFAULT",
        badgesPageVisible: true,
        visitsRead: "none",
        visitsCreate: "none",
        visitsEdit: "none",
        badgesRead: "any",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "none",
        badgeRequestsInbox: true,
        badgeRequestsIssueApproved: false,
        securityHawkEye: false,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: true,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: true,
        peopleCreateVisitor: false,
        peopleCreateSentinel: false,
        devicesManageAssignments: false,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
  {
    code: "INSTITUTION_SUPERVISOR",
    label: "Superviseur d'organisation",
    description: "Supervise les demandes de badges pour son organisation et valide leur étape.",
    permissions: {
      dashboard: "none",
      visits: "none",
      badges: "read",
      badgeTemplates: "none",
      badgeRequests: "full",
      security: "none",
      catalogs: "none",
      people: "read",
      reports: "none",
      devices: "none",
      settings: "none",
      admin: false,
      policy: {
        scope: "ORGANIZATION",
        shell: "DEFAULT",
        badgesPageVisible: true,
        visitsRead: "organization",
        visitsCreate: "none",
        visitsEdit: "none",
        badgesRead: "any",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "none",
        badgeRequestsInbox: true,
        badgeRequestsIssueApproved: false,
        securityHawkEye: true,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: false,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: true,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: false,
        peopleCreateSentinel: false,
        devicesManageAssignments: false,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
  {
    code: "BADGE_OPERATOR",
    label: "Opérateur badge",
    description: "Prépare, remet, révoque et remplace les badges.",
    permissions: {
      dashboard: "none",
      visits: "none",
      badges: "full",
      badgeTemplates: "full",
      badgeRequests: "full",
      security: "none",
      catalogs: "none",
      people: "none",
      reports: "none",
      devices: "none",
      settings: "none",
      admin: false,
      policy: {
        scope: "CENTRAL",
        shell: "DEFAULT",
        badgesPageVisible: true,
        visitsRead: "none",
        visitsCreate: "none",
        visitsEdit: "none",
        badgesRead: "any",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "none",
        badgeRequestsInbox: true,
        badgeRequestsIssueApproved: true,
        securityHawkEye: true,
        securityReadSensitive: false,
        badgesIssue: true,
        badgesManageExisting: true,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: false,
        badgeInitialEnrollmentExecute: true,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: false,
        peopleCreateSentinel: false,
        devicesManageAssignments: false,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: ["visitor"],
  },
  {
    code: "SENTINEL",
    label: "Sentinelle",
    description: "Gère l'accueil, les visites et les points de contrôle.",
    permissions: {
      dashboard: "none",
      visits: "full",
      badges: "read",
      badgeTemplates: "none",
      badgeRequests: "none",
      security: "none",
      catalogs: "none",
      people: "read",
      reports: "none",
      devices: "none",
      settings: "none",
      admin: false,
      policy: {
        scope: "CENTRAL",
        shell: "SENTINEL",
        badgesPageVisible: false,
        visitsRead: "any",
        visitsCreate: "any",
        visitsEdit: "any",
        badgesRead: "any",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "none",
        badgeRequestsInbox: false,
        badgeRequestsIssueApproved: false,
        securityHawkEye: false,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: true,
        badgeInitialEnrollmentManage: false,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: true,
        peopleCreateSentinel: false,
        devicesManageAssignments: false,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
  {
    code: "INSTITUTION_OPERATOR",
    label: "Opérateur d'organisation",
    description: "Gère les personnes et demandes de badges de son organisation.",
    permissions: {
      dashboard: "none",
      visits: "none",
      badges: "read",
      badgeTemplates: "none",
      badgeRequests: "full",
      security: "none",
      catalogs: "none",
      people: "full",
      reports: "none",
      devices: "none",
      settings: "none",
      admin: false,
      policy: {
        scope: "ORGANIZATION",
        shell: "DEFAULT",
        badgesPageVisible: true,
        visitsRead: "any",
        visitsCreate: "none",
        visitsEdit: "none",
        badgesRead: "any",
        badgeRequestsCreate: "organization",
        badgeRequestsCancel: "own",
        badgeRequestsInbox: false,
        badgeRequestsIssueApproved: false,
        securityHawkEye: true,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: false,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: false,
        peopleCreateSentinel: false,
        devicesManageAssignments: false,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
  {
    code: "MILITARY_SUPERVISOR",
    label: "Superviseur militaire",
    description: "Supervise les badges, le répertoire et les appareils au niveau central.",
    permissions: {
      dashboard: "read",
      visits: "none",
      badges: "read",
      badgeTemplates: "none",
      badgeRequests: "none",
      security: "read",
      catalogs: "full",
      people: "read",
      reports: "read",
      devices: "full",
      settings: "none",
      admin: false,
      policy: {
        scope: "CENTRAL",
        shell: "DEFAULT",
        badgesPageVisible: true,
        visitsRead: "none",
        visitsCreate: "none",
        visitsEdit: "none",
        badgesRead: "any",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "none",
        badgeRequestsInbox: false,
        badgeRequestsIssueApproved: false,
        securityHawkEye: true,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: false,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: false,
        peopleCreateSentinel: true,
        devicesManageAssignments: true,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
  {
    code: "EMPLOYEE",
    label: "Employé",
    description: "Employé de l'organisation gérant ses propres visites et ses participants.",
    permissions: {
      dashboard: "none",
      visits: "read",
      badges: "read",
      badgeTemplates: "none",
      badgeRequests: "none",
      security: "none",
      catalogs: "none",
      people: "none",
      reports: "none",
      devices: "none",
      settings: "none",
      admin: false,
      policy: {
        scope: "ORGANIZATION",
        shell: "EMPLOYEE",
        badgesPageVisible: false,
        visitsRead: "own",
        visitsCreate: "own",
        visitsEdit: "own",
        badgesRead: "own",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "own",
        badgeRequestsInbox: false,
        badgeRequestsIssueApproved: false,
        securityHawkEye: false,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: false,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: false,
        peopleCreateSentinel: false,
        devicesManageAssignments: false,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
  {
    code: "DSI",
    label: "DSI",
    description: "Équipe technique gérant les référentiels, les terminaux et les paramètres de la plateforme.",
    permissions: {
      dashboard: "none",
      visits: "read",
      badges: "none",
      badgeTemplates: "none",
      badgeRequests: "none",
      security: "none",
      catalogs: "full",
      people: "full",
      reports: "read",
      devices: "full",
      settings: "full",
      admin: false,
      policy: {
        scope: "CENTRAL",
        shell: "DEFAULT",
        badgesPageVisible: false,
        visitsRead: "own",
        visitsCreate: "own",
        visitsEdit: "own",
        badgesRead: "none",
        badgeRequestsCreate: "none",
        badgeRequestsCancel: "none",
        badgeRequestsInbox: false,
        badgeRequestsIssueApproved: false,
        securityHawkEye: true,
        securityReadSensitive: false,
        badgesIssue: false,
        badgesManageExisting: false,
        badgesReturnVisitor: false,
        badgeInitialEnrollmentManage: true,
        badgeInitialEnrollmentExecute: false,
        badgeRequestsApproveOrg: false,
        badgeRequestsApproveCabinet: false,
        peopleCreateVisitor: false,
        peopleCreateSentinel: false,
        devicesManageAssignments: true,
        linkedPersonProfileEdit: false,
      },
    },
    badgeCreationKinds: [],
  },
] as const;

export const ROLE_DEFINITIONS_BY_CODE = new Map(
  ROLE_DEFINITIONS.map((role) => [role.code, role] as const)
);

export const ROLE_POLICIES: Record<CanonicalRoleCode, RolePolicy> = {
  BADGE_SUPERVISOR: {
    allowedCategories: ["PRESIDENCY_STAFF", "MILITARY"],
    scope: "CENTRAL",
  },
  INSTITUTION_SUPERVISOR: {
    allowedCategories: ["EXTERNAL_STAFF", "PRESIDENCY_STAFF", "MILITARY", "CONTRACTOR"],
    scope: "ORGANIZATION",
  },
  BADGE_OPERATOR: {
    allowedCategories: ["PRESIDENCY_STAFF", "MILITARY"],
    scope: "CENTRAL",
  },
  SENTINEL: {
    allowedCategories: ["MILITARY"],
    scope: "CENTRAL",
  },
  INSTITUTION_OPERATOR: {
    allowedCategories: ["EXTERNAL_STAFF", "PRESIDENCY_STAFF", "MILITARY", "CONTRACTOR"],
    scope: "ORGANIZATION",
  },
  MILITARY_SUPERVISOR: {
    allowedCategories: ["MILITARY"],
    scope: "CENTRAL",
  },
  EMPLOYEE: {
    allowedCategories: ["PRESIDENCY_STAFF", "EXTERNAL_STAFF", "MILITARY"],
    scope: "ORGANIZATION",
  },
  DSI: {
    allowedCategories: ["PRESIDENCY_STAFF"],
    scope: "CENTRAL",
  },
};

export const NON_AUTH_CATEGORIES: readonly PersonCategory[] = ["VISITOR"];

export const ROLE_ASSIGNMENT_POLICIES: Record<CanonicalRoleCode, RoleAssignmentPolicy> = {
  BADGE_SUPERVISOR: {
    peopleCreationRoles: ["BADGE_SUPERVISOR", "BADGE_OPERATOR", "MILITARY_SUPERVISOR", "SENTINEL"],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [
      "BADGE_SUPERVISOR",
      "BADGE_OPERATOR",
      "MILITARY_SUPERVISOR",
      "DSI",
      "INSTITUTION_SUPERVISOR",
      "INSTITUTION_OPERATOR",
    ],
  },
  INSTITUTION_SUPERVISOR: {
    peopleCreationRoles: ["INSTITUTION_SUPERVISOR", "INSTITUTION_OPERATOR", "EMPLOYEE"],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [],
  },
  BADGE_OPERATOR: {
    peopleCreationRoles: ["BADGE_SUPERVISOR", "BADGE_OPERATOR", "MILITARY_SUPERVISOR", "SENTINEL"],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [],
  },
  SENTINEL: {
    peopleCreationRoles: [],
    canCreateVisitorFromPeople: true,
    adminAssignableRoles: [],
  },
  INSTITUTION_OPERATOR: {
    peopleCreationRoles: ["INSTITUTION_SUPERVISOR", "INSTITUTION_OPERATOR", "EMPLOYEE"],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [],
  },
  MILITARY_SUPERVISOR: {
    peopleCreationRoles: ["SENTINEL"],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [],
  },
  EMPLOYEE: {
    peopleCreationRoles: [],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [],
  },
  DSI: {
    peopleCreationRoles: [],
    canCreateVisitorFromPeople: false,
    adminAssignableRoles: [
      "BADGE_SUPERVISOR",
      "BADGE_OPERATOR",
      "MILITARY_SUPERVISOR",
      "SENTINEL",
      "DSI",
      "EMPLOYEE",
      "INSTITUTION_SUPERVISOR",
      "INSTITUTION_OPERATOR",
    ],
  },
};

export const PAGE_REQUIREMENTS: Record<PageKey, PermissionRequirement> = {
  dashboard: { kind: "permission", domain: "dashboard", minimum: "read" },
  visits: { kind: "permission", domain: "visits", minimum: "read" },
  badges: { kind: "permission", domain: "badges", minimum: "read" },
  badgeIssuance: { kind: "permission", domain: "badges", minimum: "full" },
  badgeInitialEnrollment: { kind: "authenticated" },
  badgeRequests: { kind: "permission", domain: "badgeRequests", minimum: "read" },
  people: { kind: "permission", domain: "people", minimum: "read" },
  devices: { kind: "permission", domain: "devices", minimum: "read" },
  hawkEye: { kind: "permission", domain: "security", minimum: "read" },
  reports: { kind: "permission", domain: "reports", minimum: "read" },
  settings: {
    kind: "anyPermission",
    checks: [
      { domain: "devices", minimum: "read" },
      { domain: "badgeTemplates", minimum: "read" },
      { domain: "settings", minimum: "read" },
      { domain: "catalogs", minimum: "read" },
    ],
  },
  settingsCatalog: { kind: "permission", domain: "catalogs", minimum: "read" },
  accessProfiles: {
    kind: "anyPermission",
    checks: [
      { domain: "catalogs", minimum: "read" },
      { domain: "badgeTemplates", minimum: "read" },
    ],
  },
  settingsOrganizations: {
    kind: "permission",
    domain: "settings",
    minimum: "full",
  },
  settingsBadgeTemplates: { kind: "permission", domain: "badgeTemplates", minimum: "read" },
  settingsAdministration: { kind: "permission", domain: "settings", minimum: "full" },
};

export function getRolePolicy(role: CanonicalRoleCode): RolePolicy {
  return ROLE_POLICIES[role];
}

export function isRoleAllowedForCategory(role: CanonicalRoleCode, category: PersonCategory) {
  return ROLE_POLICIES[role].allowedCategories.includes(category);
}

export function isRoleOrgScoped(role: CanonicalRoleCode) {
  return ROLE_POLICIES[role].scope === "ORGANIZATION";
}

export function isPermissionPolicyOrgScoped(policy: Pick<PermissionPolicy, "scope"> | null | undefined) {
  return policy?.scope === "ORGANIZATION";
}

export function canCategoryAuthenticate(category: PersonCategory) {
  return !NON_AUTH_CATEGORIES.includes(category);
}

export function getAssignableRolesForSurface(
  actorRole: CanonicalRoleCode,
  surface: RoleCreationSurface
) {
  return [
    ...(surface === "PEOPLE"
      ? ROLE_ASSIGNMENT_POLICIES[actorRole].peopleCreationRoles
      : ROLE_ASSIGNMENT_POLICIES[actorRole].adminAssignableRoles),
  ];
}

export function canAssignRoleOnSurface(
  actorRole: CanonicalRoleCode,
  targetRole: CanonicalRoleCode,
  surface: RoleCreationSurface
) {
  return getAssignableRolesForSurface(actorRole, surface).includes(targetRole);
}

export function canAssignRoleOnAdminSurface(
  actorRole: CanonicalRoleCode,
  targetRole: CanonicalRoleCode,
  roleKind: "SYSTEM_PRESET" | "CUSTOM"
) {
  return roleKind === "CUSTOM" || canAssignRoleOnSurface(actorRole, targetRole, "ADMIN");
}

export function canCreateVisitorFromPeopleSurface(actorRole: CanonicalRoleCode) {
  return ROLE_ASSIGNMENT_POLICIES[actorRole].canCreateVisitorFromPeople;
}

function normalizeAccessLevel(value: unknown, fallback: AccessLevel): AccessLevel {
  return value === "none" || value === "read" || value === "full" ? value : fallback;
}

function normalizeScopeAccess(value: unknown, fallback: ScopeAccess): ScopeAccess {
  return value === "none" || value === "own" || value === "organization" || value === "any" ? value : fallback;
}

function normalizePermissionPolicy(value: unknown, fallback: PermissionPolicy): PermissionPolicy {
  if (typeof value !== "object" || value === null) {
    return fallback;
  }

  const candidate = value as Partial<PermissionPolicy>;
  return {
    scope: candidate.scope === "CENTRAL" || candidate.scope === "ORGANIZATION" ? candidate.scope : fallback.scope,
    shell:
      candidate.shell === "DEFAULT" || candidate.shell === "SENTINEL" || candidate.shell === "EMPLOYEE"
        ? candidate.shell
        : fallback.shell,
    badgesPageVisible:
      typeof candidate.badgesPageVisible === "boolean" ? candidate.badgesPageVisible : fallback.badgesPageVisible,
    visitsRead: normalizeScopeAccess(candidate.visitsRead, fallback.visitsRead),
    visitsCreate: normalizeScopeAccess(candidate.visitsCreate, fallback.visitsCreate),
    visitsEdit: normalizeScopeAccess(candidate.visitsEdit, fallback.visitsEdit),
    badgesRead: normalizeScopeAccess(candidate.badgesRead, fallback.badgesRead),
    badgeRequestsCreate: normalizeScopeAccess(candidate.badgeRequestsCreate, fallback.badgeRequestsCreate),
    badgeRequestsCancel: normalizeScopeAccess(candidate.badgeRequestsCancel, fallback.badgeRequestsCancel),
    badgeRequestsInbox:
      typeof candidate.badgeRequestsInbox === "boolean" ? candidate.badgeRequestsInbox : fallback.badgeRequestsInbox,
    badgeRequestsIssueApproved:
      typeof candidate.badgeRequestsIssueApproved === "boolean"
        ? candidate.badgeRequestsIssueApproved
        : fallback.badgeRequestsIssueApproved,
    securityHawkEye: typeof candidate.securityHawkEye === "boolean" ? candidate.securityHawkEye : fallback.securityHawkEye,
    securityReadSensitive:
      typeof candidate.securityReadSensitive === "boolean" ? candidate.securityReadSensitive : fallback.securityReadSensitive,
    badgesIssue: typeof candidate.badgesIssue === "boolean" ? candidate.badgesIssue : fallback.badgesIssue,
    badgesManageExisting:
      typeof candidate.badgesManageExisting === "boolean"
        ? candidate.badgesManageExisting
        : fallback.badgesManageExisting,
    badgesReturnVisitor: typeof candidate.badgesReturnVisitor === "boolean" ? candidate.badgesReturnVisitor : fallback.badgesReturnVisitor,
    badgeInitialEnrollmentManage:
      typeof candidate.badgeInitialEnrollmentManage === "boolean"
        ? candidate.badgeInitialEnrollmentManage
        : fallback.badgeInitialEnrollmentManage,
    badgeInitialEnrollmentExecute:
      typeof candidate.badgeInitialEnrollmentExecute === "boolean"
        ? candidate.badgeInitialEnrollmentExecute
        : fallback.badgeInitialEnrollmentExecute,
    badgeRequestsApproveOrg:
      typeof candidate.badgeRequestsApproveOrg === "boolean" ? candidate.badgeRequestsApproveOrg : fallback.badgeRequestsApproveOrg,
    badgeRequestsApproveCabinet:
      typeof candidate.badgeRequestsApproveCabinet === "boolean"
        ? candidate.badgeRequestsApproveCabinet
        : fallback.badgeRequestsApproveCabinet,
    peopleCreateVisitor:
      typeof candidate.peopleCreateVisitor === "boolean" ? candidate.peopleCreateVisitor : fallback.peopleCreateVisitor,
    peopleCreateSentinel:
      typeof candidate.peopleCreateSentinel === "boolean"
        ? candidate.peopleCreateSentinel
        : fallback.peopleCreateSentinel,
    devicesManageAssignments:
      typeof candidate.devicesManageAssignments === "boolean"
        ? candidate.devicesManageAssignments
        : fallback.devicesManageAssignments,
    linkedPersonProfileEdit: false,
  };
}

export function normalizeRoleCode(role: string | null | undefined): CanonicalRoleCode {
  if (!role) {
    return "EMPLOYEE";
  }

  return (CANONICAL_ROLE_CODES as readonly string[]).includes(role)
    ? (role as CanonicalRoleCode)
    : "EMPLOYEE";
}

export function getRolePermissions(role: CanonicalRoleCode): PermissionPayload {
  return ROLE_DEFINITIONS_BY_CODE.get(role)?.permissions ?? DEFAULT_PERMISSIONS;
}

export function getUserPermissions(value: unknown): PermissionPayload {
  if (!value || typeof value !== "object") {
    return DEFAULT_PERMISSIONS;
  }

  const userRecord = value as Record<string, unknown>;
  const role = normalizeRoleCode(typeof userRecord.role === "string" ? userRecord.role : null);
  const fallback = getRolePermissions(role);
  return normalizePermissionPayload(userRecord.permissions, fallback);
}

export function getPermissionPolicy(value: Pick<PermissionPayload, "policy"> | null | undefined): PermissionPolicy {
  return value?.policy ?? DEFAULT_PERMISSIONS.policy;
}

export function getAllowedBadgeCreationKinds(role: CanonicalRoleCode): BadgeCreationKind[] {
  return [...(ROLE_DEFINITIONS_BY_CODE.get(role)?.badgeCreationKinds ?? [])];
}

export function canCreateVisitorFromPolicy(policy: PermissionPolicy) {
  return policy.peopleCreateVisitor;
}

export function canSelectVisitorsForVisit(permissions: PermissionPayload) {
  return (
    permissions.admin ||
    hasRequiredAccess(permissions.visits, "full") ||
    hasRequiredScopeAccess(permissions.policy.visitsCreate, "own") ||
    hasRequiredScopeAccess(permissions.policy.visitsEdit, "own")
  );
}

export function canCreateSentinelFromPolicy(policy: PermissionPolicy) {
  return policy.peopleCreateSentinel;
}

export function canCreateBadgeRequestFromPolicy(policy: PermissionPolicy) {
  return policy.badgeRequestsCreate !== "none";
}

export function canApproveOrgBadgeRequestFromPolicy(policy: PermissionPolicy) {
  return policy.badgeRequestsApproveOrg;
}

export function canApproveCabinetBadgeRequestFromPolicy(policy: PermissionPolicy) {
  return policy.badgeRequestsApproveCabinet;
}

export function canIssueBadgesFromPolicy(policy: PermissionPolicy) {
  return policy.badgesIssue;
}

export function canManageExistingBadgesFromPolicy(policy: PermissionPolicy) {
  return policy.badgesManageExisting;
}

export function canReturnVisitorBadgeFromPolicy(policy: PermissionPolicy) {
  return policy.badgesReturnVisitor;
}

export function canManageInitialBadgeEnrollmentFromPolicy(policy: PermissionPolicy) {
  return policy.badgeInitialEnrollmentManage;
}

export function canExecuteInitialBadgeEnrollmentFromPolicy(policy: PermissionPolicy) {
  return policy.badgeInitialEnrollmentExecute;
}

export function canViewSensitiveSecurityEventsFromPolicy(policy: PermissionPolicy) {
  return policy.securityReadSensitive;
}

export function getBadgeRequestApprovalStep(stepOrder: 1 | 2): BadgeRequestApprovalStepDefinition {
  return BADGE_REQUEST_APPROVAL_STEPS.find((step) => step.stepOrder === stepOrder) ?? BADGE_REQUEST_APPROVAL_STEPS[0];
}

export function getBadgeRequestApprovalStepByPermission(
  requiredPermission: string | null | undefined
): BadgeRequestApprovalStepDefinition | null {
  return (
    BADGE_REQUEST_APPROVAL_STEPS.find((step) => step.requiredPermission === requiredPermission) ?? null
  );
}

export function normalizePermissionPayload(
  value: unknown,
  fallback: PermissionPayload
): PermissionPayload {
  if (typeof value !== "object" || value === null) {
    return fallback;
  }

  const candidate = value as Partial<PermissionPayload>;
  return {
    dashboard: normalizeAccessLevel(candidate.dashboard, fallback.dashboard),
    visits: normalizeAccessLevel(candidate.visits, fallback.visits),
    badges: normalizeAccessLevel(candidate.badges, fallback.badges),
    badgeTemplates: normalizeAccessLevel(candidate.badgeTemplates, fallback.badgeTemplates),
    badgeRequests: normalizeAccessLevel(candidate.badgeRequests, fallback.badgeRequests),
    security: normalizeAccessLevel(candidate.security, fallback.security),
    catalogs: normalizeAccessLevel(candidate.catalogs, fallback.catalogs),
    people: normalizeAccessLevel(candidate.people, fallback.people),
    reports: normalizeAccessLevel(candidate.reports, fallback.reports),
    devices: normalizeAccessLevel(candidate.devices, fallback.devices),
    settings: normalizeAccessLevel(candidate.settings, fallback.settings),
    admin: typeof candidate.admin === "boolean" ? candidate.admin : fallback.admin,
    policy: normalizePermissionPolicy(candidate.policy, fallback.policy),
  };
}

export function hasRequiredAccess(current: AccessLevel, minimum: AccessLevel) {
  const order: AccessLevel[] = ["none", "read", "full"];
  return order.indexOf(current) >= order.indexOf(minimum);
}

export function hasRequiredScopeAccess(current: ScopeAccess, minimum: ScopeAccess) {
  const order: ScopeAccess[] = ["none", "own", "organization", "any"];
  return order.indexOf(current) >= order.indexOf(minimum);
}
